"use client";
import classNames from "classnames";
import { useRef, useState } from "react";
import { Swiper } from "swiper/react";
import { Swiper as SwiperType, Navigation, SwiperOptions } from "swiper";
import AscButton from "../AscButton/AscButton";
import { CarouselArrowLeft, CarouselArrowRight } from "../AscIcons";
import styles from "./AscAdminCarouselList.module.scss";
import AscHeading from "../AscTypography/AscHeading/AscHeading";

export interface AscCarouselListProps {
  title?: string;
  children?: React.ReactNode;
  addButtonText?: string;
  addButtonHandler?: (value?: any) => void;
  className?: string;
  classNameSwiper?: string;
  hideControls?: boolean;
  loading?: boolean;
  spaceBetween?: number;
  getSwiper?: (swiper: SwiperType) => void;
  slideChange?: (swiper: SwiperType) => void;
  breakpoints?: {
    [width: number]: SwiperOptions;
    [ratio: string]: SwiperOptions;
  };
}

export default function AscAdminCarouselList({
  title,
  children,
  className,
  classNameSwiper,
  addButtonText,
  addButtonHandler,
  hideControls = false,
  loading = false,
  spaceBetween,
  getSwiper,
  slideChange,
  breakpoints,
}: AscCarouselListProps) {
  const swiperRef = useRef<SwiperType>();
  const [isNextSlide, setIsNextSlide] = useState<boolean>();
  const [isPrevSlide, setIsPrevSlide] = useState<boolean>(true);

  const handleSlideLeft = () => swiperRef.current?.slidePrev();

  const handleSlideRight = () => swiperRef.current?.slideNext();

  const handleSlideChange = (swiper: SwiperType) => {
    setIsNextSlide(swiper.isEnd);
    setIsPrevSlide(swiper.isBeginning);
  };

  const handleBeforeInit = (swiper: SwiperType) => {
    swiperRef.current = swiper;
    handleSlideChange(swiper);
    if (getSwiper) {
      getSwiper(swiper);
    }
  };

  return (
    <div
      data-testid="asc-admin-carousel-list"
      className={classNames(
        styles.ground,
        {
          [styles.loading]: loading,
        },
        className
      )}
    >
      <div className={styles.topBar}>
        {title && <AscHeading className={styles.title}>{title}</AscHeading>}

        {!hideControls && (
          <div className={styles.navigation}>
            {addButtonText && (
              <AscButton
                onClick={addButtonHandler}
                className={styles.link}
                variant="link"
                hideLinkIcon={true}
              >
                {addButtonText} <span>+</span>
              </AscButton>
            )}

            <div className={styles.buttons}>
              <AscButton
                variant="white"
                className={styles.button}
                icon={<CarouselArrowLeft />}
                disabled={isPrevSlide || loading}
                onClick={handleSlideLeft}
              />
              <AscButton
                variant="white"
                className={styles.button}
                icon={<CarouselArrowRight />}
                disabled={isNextSlide || loading}
                onClick={handleSlideRight}
              />
            </div>
          </div>
        )}
      </div>
      <Swiper
        spaceBetween={spaceBetween ? spaceBetween : 20}
        breakpoints={
          breakpoints
            ? breakpoints
            : {
                700: {
                  slidesPerView: 1,
                },
                1200: {
                  slidesPerView: 2,
                },
                1420: {
                  slidesPerView: 3,
                },
              }
        }
        modules={[Navigation]}
        onBeforeInit={handleBeforeInit}
        observer
        observeParents
        onSlideChange={slideChange ? slideChange : handleSlideChange}
        className={classNames(styles.cardField, classNameSwiper)}
      >
        {children}
      </Swiper>
    </div>
  );
}
